export type TensionLevel = 'normal' | 'acelerado' | 'inminente';
export type AudioPhase = 'idle' | 'playing' | 'paused' | 'exploded';

export interface AudioSnapshotView {
  state: string;
  paused: boolean;
  tensionState: TensionLevel;
  bombNumber: number;
  remainingTimeMs: number;
  /** Titular actual de la bomba (para detectar una devolución). */
  currentPlayerId: string | null;
  /** Milisegundos que quedan de la ventana de devolución (0 si está cerrada). */
  returnWindowMsRemaining: number;
}

const TENSION_RATE: Record<TensionLevel, number> = {
  normal: 1.0,
  acelerado: 1.2,
  inminente: 1.5,
};

const TENSION_VOLUME: Record<TensionLevel, number> = {
  normal: 0.5,
  acelerado: 0.6,
  inminente: 0.75,
};

const BEAT_INTERVAL_MS: Record<TensionLevel, number> = {
  normal: 1000,
  acelerado: 700,
  inminente: 450,
};

const HEARTBEAT_FADE_OUT_MS = 200;
const SOUND_ENABLED_KEY = 'juegazo.soundEnabled';

/**
 * Una sola clase centraliza todo el audio del juego.
 * - Un único loop de latidos (creado la primera vez, reutilizado siempre)
 * - Una única instancia del sonido de explosion (con epoch para evitar stacks)
 * - Política de "single play" estricta via explosionEpoch
 */
class AudioManager {
  private heartbeat: HTMLAudioElement | null = null;
  private boom: HTMLAudioElement | null = null;
  private returnAudio: HTMLAudioElement | null = null;
  private enabled = AudioManager.readPersistedEnabled();
  private phase: AudioPhase = 'idle';
  private tension: TensionLevel = 'normal';
  private explosionEpoch = 0;
  private activeEpoch = 0;
  private bombKey = '';
  /** Último titular y ventana de devolución vistos: para detectar devoluciones. */
  private lastHolder: string | null = null;
  private lastReturnWindowMs = 0;
  private fadeTimer: number | null = null;
  private fadeRafId: number | null = null;
  private beatTimer: number | null = null;
  private isBeating = false;

  private static readPersistedEnabled(): boolean {
    try {
      const raw = window.localStorage.getItem(SOUND_ENABLED_KEY);
      if (raw === 'false') return false;
      if (raw === 'true') return true;
    } catch {
      // ignore
    }
    // Default: muted for first-time users. They can opt in via the sound
    // toggle in the intro or game view. Defaulting to unmuted made the toggle
    // visually show "sound on" while no audio actually played.
    return false;
  }

  private static persistEnabled(enabled: boolean): void {
    try {
      window.localStorage.setItem(SOUND_ENABLED_KEY, String(enabled));
    } catch {
      // ignore
    }
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    AudioManager.persistEnabled(enabled);
    if (!enabled) {
      this.stopAll();
    } else if (this.phase === 'playing') {
      this.startHeartbeat(this.tension);
    }
  }

  /**
   * Carga el audio del heartbeat. Llamar una vez desde el primer click del
   * usuario para que el browser lo autorice a sonar. Es idempotente.
   */
  unlock(): void {
    if (this.heartbeat) return;
    const audio = new Audio('/sounds/heartbeat.mp3');
    audio.preload = 'auto';
    audio.volume = 0;
    audio.playbackRate = TENSION_RATE[this.tension];
    const p = audio.play();
    if (p) {
      p.then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = TENSION_VOLUME[this.tension];
      }).catch(() => {
        audio.volume = TENSION_VOLUME[this.tension];
      });
    } else {
      audio.volume = TENSION_VOLUME[this.tension];
    }
    this.heartbeat = audio;

    // Pre-load explosion audio so it's decoded and ready
    if (!this.boom) {
      const boom = new Audio('/sounds/explosion.mp3');
      boom.preload = 'auto';
      boom.volume = 0.9;
      boom.load();
      this.boom = boom;
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Entry point llamado por la vista del juego cada vez que cambia el snapshot.
   * El manager decide basado en la fase qué reproducir. No produce sonido duplicado.
   */
  sync(snapshot: AudioSnapshotView): void {
    if (this.detectBombReturn(snapshot)) {
      this.playReturn();
    }
    const state = snapshot.state;
    const paused = snapshot.paused;
    const newTension = snapshot.tensionState;
    const newKey = `${snapshot.bombNumber}|${state}`;

    const newBomb = newKey !== this.bombKey && (state === 'playing' || state === 'tiebreak_playing');
    if (newBomb) {
      this.bombKey = newKey;
      this.activeEpoch++;
      this.explosionEpoch = 0;
      this.cancelFadeOut();
    }

    const isPlaying = state === 'playing' || state === 'tiebreak_playing';
    const isExploded = state === 'exploded';

    if (isPlaying) {
      if (paused) {
        if (this.phase !== 'paused') {
          this.phase = 'paused';
          this.pauseAll();
        }
      } else {
        if (this.phase !== 'playing' || newBomb) {
          this.phase = 'playing';
          this.startHeartbeat(newTension);
          this.tension = newTension;
        } else if (this.tension !== newTension) {
          this.applyTension(newTension);
          this.tension = newTension;
        }
      }
    } else if (isExploded) {
      if (this.phase !== 'exploded') {
        this.phase = 'exploded';
        this.fadeOutHeartbeats();
        this.fireExplosionIfNeeded();
      }
    } else {
      if (this.phase !== 'idle') {
        this.phase = 'idle';
        this.stopAll();
      }
    }
  }

  teardown(): void {
    this.stopAll();
    this.cancelFadeOut();
    this.cancelBeatTimer();
    if (this.heartbeat) {
      this.heartbeat.removeAttribute('src');
      this.heartbeat.load();
      this.heartbeat = null;
    }
    if (this.boom) {
      this.boom.removeAttribute('src');
      this.boom.load();
      this.boom = null;
    }
    this.phase = 'idle';
    this.tension = 'normal';
    this.bombKey = '';
    this.explosionEpoch = 0;
    this.activeEpoch = 0;
    this.lastHolder = null;
    this.lastReturnWindowMs = 0;
    if (this.returnAudio) {
      this.returnAudio.pause();
      this.returnAudio.currentTime = 0;
    }
  }

  private startHeartbeat(tension: TensionLevel): void {
    if (!this.enabled) return;
    if (!this.heartbeat) {
      const audio = new Audio('/sounds/heartbeat.mp3');
      audio.preload = 'auto';
      this.heartbeat = audio;
    }
    const hb = this.heartbeat;
    hb.playbackRate = TENSION_RATE[tension];
    hb.volume = TENSION_VOLUME[tension];
    this.tension = tension;
    this.cancelFadeOut();
    this.cancelBeatTimer();
    this.scheduleNextBeat();
  }

  private applyTension(tension: TensionLevel): void {
    if (!this.heartbeat) return;
    // Solo actualizamos this.tension. El cambio de playbackRate/volume se
    // difiere al proximo playSingleBeat, evitando corte mid-clip.
    // NO cancelamos el beatTimer: preserva el ritmo del latido pendiente.
    this.tension = tension;
  }

  private scheduleNextBeat(): void {
    if (!this.enabled) return;
    if (this.phase !== 'playing') return;
    this.cancelBeatTimer();
    const interval = BEAT_INTERVAL_MS[this.tension];
    const epochAtSchedule = this.activeEpoch;
    const manager = this;
    this.beatTimer = window.setTimeout(() => {
      if (manager.activeEpoch !== epochAtSchedule) return;
      manager.playSingleBeat();
    }, interval);
  }

  private playSingleBeat(): void {
    if (!this.enabled) return;
    if (this.phase !== 'playing') return;
    if (this.explosionEpoch > 0) return;
    if (!this.heartbeat) return;
    const hb = this.heartbeat;
    try {
      hb.currentTime = 0;
    } catch {
      // ignore
    }
    hb.playbackRate = TENSION_RATE[this.tension];
    hb.volume = TENSION_VOLUME[this.tension];
    const p = hb.play();
    if (p) p.catch(() => undefined);
    this.scheduleNextBeat();
  }

  private cancelBeatTimer(): void {
    if (this.beatTimer !== null) {
      clearTimeout(this.beatTimer);
      this.beatTimer = null;
    }
  }

  private pauseAll(): void {
    this.cancelBeatTimer();
    if (this.heartbeat) this.heartbeat.pause();
  }

  private stopAll(): void {
    this.cancelFadeOut();
    this.cancelBeatTimer();
    if (this.heartbeat) {
      this.heartbeat.pause();
      this.heartbeat.currentTime = 0;
    }
    if (this.boom) {
      this.boom.pause();
      this.boom.currentTime = 0;
    }
  }

  /**
   * ¿Entre el snapshot anterior y este la bomba volvió a su titular anterior?
   *
   * La señal es el cierre de la ventana de devolución (quedaban segundos y ahora
   * no queda ninguno) junto al cambio de titular, con la partida todavía en
   * juego: cuando la ventana expira sola el titular no cambia, y cuando la
   * bomba explota el estado ya no es "playing".
   */
  private detectBombReturn(snapshot: AudioSnapshotView): boolean {
    const prevHolder = this.lastHolder;
    const prevWindow = this.lastReturnWindowMs;
    this.lastHolder = snapshot.currentPlayerId;
    this.lastReturnWindowMs = snapshot.returnWindowMsRemaining;

    if (prevHolder === null) return false;
    if (prevWindow <= 0) return false;
    if (snapshot.returnWindowMsRemaining > 0) return false;
    if (prevHolder === snapshot.currentPlayerId) return false;
    if (snapshot.state !== 'playing' && snapshot.state !== 'tiebreak_playing') return false;
    return true;
  }

  /** Reproduce el sonido de devolución de la bomba (distinto del de explosión). */
  playReturn(): void {
    if (!this.enabled) return;
    if (!this.returnAudio) {
      const audio = new Audio('/sounds/return-bomb.mp3');
      audio.preload = 'auto';
      audio.volume = 0.6;
      this.returnAudio = audio;
    }
    const sound = this.returnAudio;
    try {
      sound.currentTime = 0;
    } catch {
      // ignore
    }
    const p = sound.play();
    if (p) p.catch(() => undefined);
  }

  private fireExplosionIfNeeded(): void {
    if (this.explosionEpoch > 0) return;
    this.fireExplosion();
  }

  private fireExplosion(): void {
    if (this.explosionEpoch > 0) return;
    if (!this.enabled) return;
    this.explosionEpoch++;
    if (!this.boom) {
      const audio = new Audio('/sounds/explosion.mp3');
      audio.preload = 'auto';
      audio.volume = 0.9;
      this.boom = audio;
    }
    const boom = this.boom;
    try {
      boom.currentTime = 0;
    } catch {
      // ignore
    }
    const p = boom.play();
    if (p) p.catch(() => undefined);
    this.fadeOutHeartbeats();
    this.cancelBeatTimer();
  }

  private fadeOutHeartbeats(): void {
    if (!this.heartbeat) return;
    this.cancelFadeOut();
    const hb = this.heartbeat;
    const startVol = hb.volume;
    const startTime = performance.now();
    const duration = HEARTBEAT_FADE_OUT_MS;
    const tick = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(1, elapsed / duration);
      if (!hb) return;
      hb.volume = Math.max(0, startVol * (1 - t));
      if (t < 1) {
        this.fadeRafId = window.setTimeout(tick, 30) as unknown as number;
      } else {
        hb.pause();
        hb.currentTime = 0;
        this.fadeRafId = null;
      }
    };
    tick();
  }

  private cancelFadeOut(): void {
    if (this.fadeTimer !== null) {
      clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
    if (this.fadeRafId !== null) {
      clearTimeout(this.fadeRafId);
      this.fadeRafId = null;
    }
  }
}

export const audioManager = new AudioManager();
